import { CalendarClock, CircleEllipsis, ShoppingBasket, Tags } from "lucide-react";
import { List } from "modules/lists/types/lists";
import MenuIcon from "modules/main/types/menus";
import { getListCategoryDefinition } from "modules/lists/utils/listCategories";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

interface ListCardProps extends React.HTMLAttributes<HTMLDivElement> {
    list: List;
    isSelected: boolean;
    onSelect: () => void;
    onEdit: () => void;
    onDelete: () => void;
}

/**
 * Displays a single list as a selectable card with overflow actions.
 * Shows the list icon, name, item count badge and relative creation date.
 */
const ListCard: React.FC<ListCardProps> = ({ list, onSelect, className }) => {
    const relativeDate = formatDistanceToNow(new Date(list.updatedAt), {
        addSuffix: true,
        locale: es,
    });
    const sortedCategorySummaries = list.categorySummaries
        .filter((summary) => summary.itemCount > 0)
        .sort((a, b) => b.itemCount - a.itemCount);
    const categoryPreviews = sortedCategorySummaries
        .slice(0, 3)
        .map((summary) => ({
            ...summary,
            category: getListCategoryDefinition(summary.categoryId ?? -1),
        }));
    const remainingItemCount = sortedCategorySummaries
        .slice(3)
        .reduce((total, summary) => total + summary.itemCount, 0);

    return (
        <Card className={cn("pb-0 gap-3 pt-2 px-0 cursor-pointer", className)} onClick={onSelect}>
            <CardHeader className="w-full flex justify-between px-2">
                <span className="text-xs text-muted-foreground">
                    <CalendarClock className="w-4 h-4 inline-block mr-1"/> {relativeDate}
                </span>
                <span className="text-xs text-muted-foreground">
                    <ShoppingBasket className="w-4 h-4 inline-block mr-1"/> {list.itemCount}
                </span>                
            </CardHeader>
            <CardContent className="flex flex-col justify-between gap-2 h-full">
                <div className="w-full">
                    <p className="text-center font-bold text-lg">{list.name}</p>
                </div>
                <div className="flex w-full items-center justify-center gap-3 overflow-hidden py-4 h-24">
                    {categoryPreviews.length === 0 ? (
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
                            <ShoppingBasket className="h-7 w-7" aria-hidden="true" />
                        </div>
                    ) : (
                        categoryPreviews.map((preview) => (
                            <div key={preview.category.id} className="relative flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                                <MenuIcon iconName={preview.category.icon} size={28} className="text-primary" />
                                <span className="absolute -right-1 -top-1 flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-bold text-primary-foreground">
                                    {preview.itemCount}
                                </span>
                            </div>
                        ))
                    )}
                    {remainingItemCount > 0 && (
                        <div className="relative flex h-14 w-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
                            <Tags className="h-7 w-7" aria-hidden="true" />
                            <span className="absolute -right-1 -top-1 flex h-6 min-w-6 items-center justify-center rounded-full bg-muted-foreground px-1.5 text-xs font-bold text-background">
                                {remainingItemCount}
                            </span>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};


export { ListCard };
